import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Empty, Pagination, Table, Tag, message } from 'antd';
import {
  FileTextOutlined,
  UploadOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import ResumeUpload from '@/components/ResumeUpload';
import ResumePreview from '@/components/ResumePreview';
import PointsGetter from '@/components/PointsGetter';
import { getResumeList, getResume, deleteResume } from '@/services/user';
import { getInterviewList } from '@/services/interview';
import type { ResumeDTO, InterviewDTO, PointsHistoryDTO, PaginationDTO } from '@/types';
import type { ReportGenerationStatus } from '@/types/interview';
import { ParseStatus } from '@/types/resume';
import { getPointsHistory } from '@/services/points';
import { ColumnType } from 'antd/es/table';
import { useSetRecoilState } from 'recoil';
import { currentWaitingReportState } from '@/store/message';

const pointsHistoryColumns: ColumnType<PointsHistoryDTO>[] = [
  {
    title: '变更原因',
    dataIndex: 'businessTypeLabel',
    key: 'businessTypeLabel',
  },
  {
    title: '变更数量',
    dataIndex: 'amount',
    key: 'amount',
    render: (amount: number, record: PointsHistoryDTO) => {
      // 假定 transactionType === 'DEDUCTION' 为减分，否则正分
      const isDeduction = record.transactionType === 'spend';
      const displayAmount = isDeduction ? `- ${amount}` : `+ ${amount}`;
      return (
        <span className={isDeduction ? 'text-red-500' : 'text-green-600'}>{displayAmount}</span>
      );
    },
  },
  {
    title: '变更时间',
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: (value: string) => {
      if (!value) return '';
      // 期望输出: 26-01-03 08:57
      const date = new Date(value);
      const yy = String(date.getFullYear()).slice(2);
      const MM = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      const hh = String(date.getHours()).padStart(2, '0');
      const mm = String(date.getMinutes()).padStart(2, '0');
      return `${yy}-${MM}-${dd} ${hh}:${mm}`;
    },
  },
];

const pageSize = 6;

const Profile = () => {
  const navigate = useNavigate();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [resume, setResume] = useState<ResumeDTO | null>(null);
  const [loadingResume, setLoadingResume] = useState(true);
  const [interviews, setInterviews] = useState<PaginationDTO<InterviewDTO>>();
  const [pointsHistoryData, setPointsHistoryData] = useState<PaginationDTO<PointsHistoryDTO>>();
  const [loadingPointsHistory, setLoadingPointsHistory] = useState(false);
  const [loadingInterviews, setLoadingInterviews] = useState(true);
  const [showPointsHistoryTable, setShowPointsHistoryTable] = useState(false);
  const setCurrentWaitingReportState = useSetRecoilState(currentWaitingReportState);
  const stopRollingRef = useRef<() => void>();
  const hasResume = !!resume;

  // 加载简历信息
  const loadResume = useCallback(async () => {
    try {
      setLoadingResume(true);
      const { items } = await getResumeList();

      if (items.length > 0) {
        setResume(items[0]);
        if ([ParseStatus.COMPLETED, ParseStatus.FAILED].indexOf(items[0].parseStatus) === -1) {
          stopRollingRef.current?.();
          stopRollingRef.current = pollResumeParseInBackground(items[0].resumeNumber);
        }
      } else {
        setResume(null);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('加载简历失败:', error);
      message.error('加载简历失败');
    } finally {
      setLoadingResume(false);
    }
  }, []);

  // 加载面试历史
  const loadInterviews = useCallback(
    async (page: number) => {
      try {
        setLoadingInterviews(true);
        const response = await getInterviewList({
          page,
          pageSize,
        });
        setInterviews(response);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('加载面试历史失败:', error);
        message.error('加载面试历史失败');
      } finally {
        setLoadingInterviews(false);
      }
    },
    [pageSize],
  );

  const loadPointsHistory = useCallback(
    async (page: number = 1) => {
      try {
        setLoadingPointsHistory(true);
        const response = await getPointsHistory({ page, pageSize });
        setPointsHistoryData(response);
        console.log(pointsHistoryData);
      } catch (error) {
        console.error('加载积分变更历史失败:', error);
        message.error('加载积分变更历史失败');
      } finally {
        setLoadingPointsHistory(false);
      }
    },
    [pageSize],
  );

  useEffect(() => {
    loadResume();
    loadInterviews(1);
    loadPointsHistory(1);
  }, [loadResume, loadInterviews, loadPointsHistory]);

  useEffect(() => {
    return () => {
      stopRollingRef.current?.();
    };
  }, []);

  // 后台轮询简历解析结果（不通过 message 干扰用户）
  const pollResumeParseInBackground = useCallback((resumeNumber: number) => {
    let cancelled = false;
    const run = async () => {
      let parseStatus: ParseStatus | undefined;
      let updatedResume: ResumeDTO | null = null;
      let retryCount = 0;
      while (!cancelled && retryCount < 60) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        if (cancelled) return;
        try {
          const resume = await getResume(resumeNumber);
          updatedResume = resume;
          parseStatus = resume.parseStatus;
          if (parseStatus === ParseStatus.COMPLETED || parseStatus === ParseStatus.FAILED) {
            break;
          }
        } catch {
          // 静默退出
          return;
        }
        retryCount += 1;
      }
      if (cancelled) return;
      if (parseStatus === ParseStatus.COMPLETED && updatedResume) {
        setResume(updatedResume);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  // 处理简历上传成功
  const handleUploadSuccess = useCallback(
    (uploadedResume: ResumeDTO) => {
      setResume(uploadedResume);
      setUploadOpen(false);

      if (uploadedResume.resumeNumber) {
        const status = uploadedResume.parseStatus;
        if (status !== ParseStatus.COMPLETED && status !== ParseStatus.FAILED) {
          stopRollingRef.current = pollResumeParseInBackground(uploadedResume.resumeNumber);
        }
      }
    },
    [pollResumeParseInBackground],
  );

  // 处理面试记录点击
  const handleInterviewClick = (interview: InterviewDTO) => {
    if (interview.status === 'completed') {
      if (interview.reportGenerationStatus !== 'completed') {
        setCurrentWaitingReportState({
          [interview.interviewNumber]: 'waiting',
        });
      }
      navigate(`/report/${interview.interviewNumber}`);
    } else {
      navigate(`/interview/${interview.interviewNumber}`);
    }
  };

  // 格式化文件大小
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // 获取解析状态标签
  const getParseStatusTag = (status: ParseStatus) => {
    switch (status) {
      case ParseStatus.COMPLETED:
        return <Tag color="success">解析完成</Tag>;
      case ParseStatus.PARSING:
        return <Tag color="geekblue">解析中</Tag>;
      case ParseStatus.FAILED:
        return <Tag color="error">解析失败</Tag>;
      default:
        return <Tag>待解析</Tag>;
    }
  };

  // 获取面试状态标签
  const getInterviewStatusTag = (status: InterviewDTO['status']) => {
    switch (status) {
      case 'completed':
        return (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            面试已完成
          </Tag>
        );
      case 'in_progress':
        return (
          <Tag color="processing" icon={<ClockCircleOutlined />}>
            面试进行中
          </Tag>
        );
      case 'skipped':
      case 'cancelled':
        return (
          <Tag color="default" icon={<CloseCircleOutlined />}>
            面试已取消
          </Tag>
        );
      default:
        return <Tag>未知</Tag>;
    }
  };

  // 获取报告生成状态标签（仅当面试已完成时展示）
  const getReportGenerationStatusTag = (reportStatus?: ReportGenerationStatus) => {
    if (!reportStatus) return null;
    switch (reportStatus) {
      case 'completed':
        return <Tag color="success">报告已生成</Tag>;
      default:
        return <Tag color="processing">报告生成中</Tag>;
    }
  };

  const handleDeleteResume = async () => {
    if (resume) {
      await deleteResume(resume?.resumeNumber);
      setResume(null);
      message.success('简历删除成功');
      loadResume();
    }
  };

  return (
    <div className="px-6 lg:px-0 max-w-[1440px] mx-auto px-6 lg:px-12">
      {/* 头部区域 */}
      <section className="relative overflow-hidden py-8">
        <div className="relative mx-auto z-[1]">
          <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">
            你好，<span className="text-indigo-500">{window.config?.username}</span>
          </h1>
          <p className="mt-3 mb-3 text-base text-slate-600">管理个人信息，简历和面试记录</p>
        </div>
      </section>

      <div className="relative flex flex-col rounded-xl border border-slate-200 bg-gradient-to-br from-amber-50 to-yellow-100 shadow-sm mb-8 z-[1]">
        <PointsGetter mode="page" className="!bg-transparent" onPaySuccess={loadPointsHistory} />

        {!showPointsHistoryTable && (
          <div className="flex justify-center px-6 pb-4">
            <Button
              type="link"
              size="small"
              onClick={() => {
                setShowPointsHistoryTable(true);
                loadPointsHistory(1);
              }}
            >
              查看账单详情
              <DownOutlined className="ml-1" />
            </Button>
          </div>
        )}

        {showPointsHistoryTable && (
          <Table
            className="flex-1 p-6"
            loading={loadingPointsHistory}
            size="small"
            dataSource={pointsHistoryData?.items || []}
            columns={pointsHistoryColumns}
            rowKey="id"
            pagination={{
              className: '!my-1',
              hideOnSinglePage: true,
              current: pointsHistoryData?.page || 1,
              pageSize: pointsHistoryData?.pageSize || 10,
              total: pointsHistoryData?.total || 0,
              showPrevNextJumpers: false,
              showSizeChanger: false,
              showQuickJumper: false,
              onChange: (page) => {
                loadPointsHistory(page);
              },
            }}
            bordered
          />
        )}
      </div>

      <div className="mx-auto">
        {/* 简历管理区域 */}
        <Card
          className="mb-8 shadow-sm z-[1]"
          title={
            <div className="flex items-center gap-2">
              <FileTextOutlined className="text-indigo-500" />
              <span>我的简历</span>
            </div>
          }
          loading={loadingResume}
        >
          <div className="flex flex-row gap-1 mb-4">
            <Button
              size="small"
              type="primary"
              icon={<UploadOutlined />}
              onClick={() => setUploadOpen(true)}
            >
              {hasResume ? '重新上传' : '上传简历'}
            </Button>
            {hasResume && (
              <Button
                type="default"
                className="ml-2"
                icon={<DeleteOutlined />}
                onClick={handleDeleteResume}
              >
                删除简历
              </Button>
            )}
          </div>
          {resume ? (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-slate-900">{resume.fileName}</h3>
                    {getParseStatusTag(resume.parseStatus)}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-slate-600">
                    <div>
                      <span className="font-medium">文件类型：</span>
                      <span>{resume.fileType}</span>
                    </div>
                    <div>
                      <span className="font-medium">文件大小：</span>
                      <span>{formatFileSize(resume.fileSize)}</span>
                    </div>
                    <div>
                      <span className="font-medium">上传时间：</span>
                      <span>
                        {new Date(resume.createdAt).toLocaleString('zh-CN', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              {/* 在线简历预览 */}
              {resume.resumeMetadata && <ResumePreview metadata={resume.resumeMetadata} />}

              {/* 原始文件查看按钮 */}
              {/* {resume.fileUrl && (
                <div className="mt-4">
                  <Button
                    type="default"
                    href={resume.fileUrl}
                    target="_blank"
                    icon={<EyeOutlined />}
                  >
                    查看原始文件
                  </Button>
                </div>
              )} */}
            </div>
          ) : (
            <Empty description="暂无简历" image={Empty.PRESENTED_IMAGE_SIMPLE}></Empty>
          )}
        </Card>

        {/* 面试历史区域 */}
        <Card
          className="mb-8 shadow-sm z-[1]"
          title={
            <div className="flex items-center gap-2">
              <ClockCircleOutlined className="text-indigo-500" />
              <span>面试历史</span>
            </div>
          }
          extra={
            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              loading={loadingInterviews}
              onClick={() => loadInterviews(interviews?.page ?? 1)}
              aria-label="刷新面试历史"
            >
              刷新
            </Button>
          }
          loading={loadingInterviews}
        >
          {interviews?.items && interviews.items.length > 0 ? (
            <>
              <div className="space-y-4">
                {interviews.items.map((interview) => (
                  <div
                    key={interview.interviewNumber}
                    className="p-4 border border-slate-200 rounded-lg hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => handleInterviewClick(interview)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2 flex-wrap">
                          <h3 className="text-base font-semibold mr-3 text-slate-900">
                            面试 #{interview.interviewNumber}
                          </h3>
                          {getInterviewStatusTag(interview.status)}
                          {interview.status === 'completed' &&
                            getReportGenerationStatusTag(interview.reportGenerationStatus)}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-slate-600">
                          {interview.company && (
                            <div>
                              <span className="font-medium">意向公司：</span>
                              <span>{interview.company}</span>
                            </div>
                          )}
                          {interview.position && (
                            <div>
                              <span className="font-medium">意向职位：</span>
                              <span>{interview.position}</span>
                            </div>
                          )}
                          <div>
                            <span className="font-medium">问题数：</span>
                            <span>
                              {interview.correctCount}/{interview.totalQuestions}
                            </span>
                          </div>
                          {interview.startedAt && (
                            <div>
                              <span className="font-medium">开始时间：</span>
                              <span>
                                {new Date(interview.startedAt).toLocaleString('zh-CN', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {interviews?.total && interviews.total > pageSize && (
                <div className="mt-6 flex justify-center">
                  <Pagination
                    current={interviews.page}
                    total={interviews.total}
                    pageSize={pageSize}
                    onChange={(page) => {
                      loadInterviews(page);
                    }}
                    showPrevNextJumpers={false}
                    showSizeChanger={false}
                    showQuickJumper={false}
                    showTotal={(total) => `共 ${total} 条记录`}
                  />
                </div>
              )}
            </>
          ) : (
            <Empty description="暂无面试记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
        </Card>
      </div>

      {/* 简历上传弹窗 */}
      <ResumeUpload
        open={uploadOpen}
        onCancel={() => setUploadOpen(false)}
        onUploadSuccess={handleUploadSuccess}
      />
    </div>
  );
};

export default Profile;
